import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable'
import { useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import Button from '@atlaskit/button'
import AddIcon from '@atlaskit/icon/glyph/add'
import {
  selectExpandedFolderIds,
  toggleFolderExpand
} from '../../store/slices/folderSlice'
import { FolderItem } from './FolderItem'

/**
 * SORTABLE FOLDER ITEM WRAPPER
 *
 * Wraps FolderItem with sortable drag-and-drop functionality.
 */
function SortableFolderItem({
  folder,
  level,
  isExpanded,
  isSelected,
  hasChildren,
  onSelect,
  onToggleExpand,
  onAddSubfolder,
  onRename,
  onDelete,
  onClone,
  onImportFromLibrary,
  onImportFromRelease
}) {
  // Use prefixed ID to distinguish folders from scenarios in drag events
  const sortableId = `folder-${folder.id}`

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: sortableId,
    data: {
      type: 'folder',
      folder: folder
    }
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  return (
    <div ref={setNodeRef} style={style}>
      <FolderItem
        folder={folder}
        level={level}
        isExpanded={isExpanded}
        isSelected={isSelected}
        hasChildren={hasChildren}
        onSelect={onSelect}
        onToggleExpand={onToggleExpand}
        onAddSubfolder={onAddSubfolder}
        onRename={onRename}
        onDelete={onDelete}
        onClone={onClone}
        onImportFromLibrary={onImportFromLibrary}
        onImportFromRelease={onImportFromRelease}
        dragHandleProps={{ ...attributes, ...listeners }}
        isDragging={isDragging}
      />
    </div>
  )
}

/**
 * FOLDER SIDEBAR COMPONENT
 *
 * Left panel of the folder browser containing:
 * - Hierarchical folder tree with expand/collapse
 * - Drag-and-drop reordering of folders
 *
 * Features:
 * - Click row to select folder
 * - Click chevron to expand/collapse (doesn't change selection)
 * - Drag to reorder folders within same level
 */
function FolderSidebar({
  folderTree = [],
  selectedFolderId,
  onFolderSelect,
  onAddSubfolder,
  onRenameFolder,
  onDeleteFolder,
  onCloneFolder,
  onImportFromLibrary,
  onImportFromRelease,
  onReorderFolders,
  releaseId
}) {
  const dispatch = useDispatch()
  const expandedFolderIds = useSelector(selectExpandedFolderIds)

  // Handle folder expand/collapse
  const handleToggleExpand = (folderId, e) => {
    e.stopPropagation() // Don't trigger folder selection
    dispatch(toggleFolderExpand(folderId))
  }

  // Handle folder selection
  const handleSelectFolder = (folderId) => {
    onFolderSelect(folderId)
  }

  // "All Scenarios" row is selected when no folder is selected (selectedFolderId === null)
  const isAllScenariosSelected = selectedFolderId === null || selectedFolderId === undefined

  // Make "All Scenarios" a drop target — dropping a scenario here clears its folder.
  // The drop is handled in FolderBrowser's handleUnifiedDragEnd (overId === 'all-scenarios-drop').
  const { isOver: isAllScenariosDropOver, setNodeRef: setAllScenariosDropRef } = useDroppable({
    id: 'all-scenarios-drop',
    data: { type: 'all-scenarios' }
  })

  // Get ALL folder IDs for SortableContext (including nested folders)
  // Use prefixed IDs to match the sortable items
  const getAllFolderIds = (folders) => {
    let ids = []
    folders.forEach(folder => {
      ids.push(`folder-${folder.id}`)
      if (folder.children && folder.children.length > 0) {
        ids = ids.concat(getAllFolderIds(folder.children))
      }
    })
    return ids
  }
  const allFolderIds = getAllFolderIds(folderTree)

  // Render folder tree recursively
  // ALL folders use SortableFolderItem for consistent drag handles and structure
  const renderFolderTree = (folders, level = 0, parentId = null) => {
    return folders.map(folder => {
      const isExpanded = expandedFolderIds.includes(folder.id)
      const isSelected = selectedFolderId === folder.id
      const hasChildren = folder.children && folder.children.length > 0

      return (
        <React.Fragment key={folder.id}>
          {/* All folders are sortable and draggable */}
          <SortableFolderItem
            folder={folder}
            level={level}
            isExpanded={isExpanded}
            isSelected={isSelected}
            hasChildren={hasChildren}
            onSelect={() => handleSelectFolder(folder.id)}
            onToggleExpand={(e) => handleToggleExpand(folder.id, e)}
            onAddSubfolder={onAddSubfolder}
            onRename={onRenameFolder}
            onDelete={onDeleteFolder}
            onClone={onCloneFolder}
            onImportFromLibrary={onImportFromLibrary}
            onImportFromRelease={onImportFromRelease}
          />
          {/* Render children if expanded */}
          {isExpanded && hasChildren && (
            <div className="folder-children">
              {renderFolderTree(folder.children, level + 1, folder.id)}
            </div>
          )}
        </React.Fragment>
      )
    })
  }

  return (
    <div
      className="folder-sidebar"
      style={{
        width: '280px',
        minWidth: '280px',
        borderRight: '1px solid var(--ds-border, #DFE1E6)',
        backgroundColor: 'var(--ds-surface, #FFFFFF)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderTopLeftRadius: '8px',
        borderBottomLeftRadius: '8px'
      }}
    >
      {/* Sidebar Header */}
      <div
        className="folder-sidebar-header"
        style={{
          padding: '8px 12px',
          borderBottom: '1px solid var(--ds-border, #DFE1E6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <span style={{
          fontSize: '11px',
          fontWeight: 600,
          color: 'var(--ds-text-subtlest, #6B778C)',
          textTransform: 'uppercase'
        }}>
          Folders
        </span>
        <Button
          appearance="subtle"
          iconBefore={<AddIcon label="Add folder" size="small" />}
          onClick={() => onAddSubfolder?.(null)}
        >
          Add folder
        </Button>
      </div>

      {/* Folder List */}
      <div
        className="folder-list"
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '4px 0'
        }}
      >
        {/* "All Scenarios" — clickable row; shows every scenario in the release,
            including unfoldered ones that result from folder deletion.
            Also a drop target — dragging a scenario here clears its folder (root). */}
        <div
          ref={setAllScenariosDropRef}
          className={`all-scenarios-row ${isAllScenariosSelected ? 'selected' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => handleSelectFolder(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleSelectFolder(null)
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: (isAllScenariosSelected || isAllScenariosDropOver) ? 600 : 400,
            color: (isAllScenariosSelected || isAllScenariosDropOver)
              ? 'var(--ds-text-selected, #0052CC)'
              : 'var(--ds-text, #172B4D)',
            // Drag-over highlight wins over the selected state
            backgroundColor: isAllScenariosDropOver
              ? 'var(--ds-background-selected-hovered, #B3D4FF)'
              : isAllScenariosSelected
                ? 'var(--ds-background-selected, #DEEBFF)'
                : 'transparent',
            outline: isAllScenariosDropOver
              ? '2px solid var(--ds-border-selected, #0052CC)'
              : 'none',
            outlineOffset: isAllScenariosDropOver ? '-2px' : 0,
            cursor: 'pointer',
            userSelect: 'none',
            borderRadius: '3px',
            margin: '0 4px',
            transition: 'background-color 120ms ease'
          }}
          onMouseEnter={(e) => {
            if (!isAllScenariosSelected && !isAllScenariosDropOver) {
              e.currentTarget.style.backgroundColor = 'var(--ds-background-neutral-subtle-hovered, #F4F5F7)'
            }
          }}
          onMouseLeave={(e) => {
            if (!isAllScenariosSelected && !isAllScenariosDropOver) {
              e.currentTarget.style.backgroundColor = 'transparent'
            }
          }}
        >
          All Scenarios
        </div>

        {/* Folder Tree with Drag-and-Drop */}
        {folderTree.length > 0 ? (
          <SortableContext
            items={allFolderIds}
            strategy={verticalListSortingStrategy}
          >
            {renderFolderTree(folderTree)}
          </SortableContext>
        ) : (
          <div style={{
            padding: '16px',
            textAlign: 'center',
            color: 'var(--ds-text-subtlest, #6B778C)',
            fontSize: '13px'
          }}>
            No folders yet
          </div>
        )}
      </div>
    </div>
  )
}

export { FolderSidebar }
